import { createContext, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { visitorService, type CreateVisitorData, type UpdateVisitorData, type Visitor } from '../../../shared/services/visitors/visitorService';

interface VisitorContextData {
  // Listagem
  visitors: Visitor[];
  isLoading: boolean;
  loading: boolean;
  error: string | null;
  handleCreate: () => void;
  handleView: (visitor: Visitor) => void;
  handleCloseError: () => void;
  
  // Modal de Delete
  isDeleteModalOpen: boolean;
  selectedVisitor: Visitor | null;
  openDeleteModal: (visitor: Visitor) => void;
  closeDeleteModal: () => void;
  handleDeleteVisitor: () => void;
  isDeleting: boolean;
  
  // Navegação
  navigateToEdit: (id: string) => void;

  // Form
  visitorData: Visitor | null;
  isDataLoading: boolean;
  createVisitor: (data: CreateVisitorData) => Promise<void>;
  updateVisitor: (id: string, data: UpdateVisitorData) => Promise<void>;
  isSubmitting: boolean;
  getVisitorById: (id: string) => Visitor;
  setError: (error: string | null) => void;
  
  // Pagination
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
}

interface VisitorProviderProps {
  children: ReactNode;
}

export const VisitorContext = createContext<VisitorContextData>({} as VisitorContextData);

export function VisitorProvider({ children }: VisitorProviderProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Query para listar visitors
  const { data: visitorsResponse, isLoading } = useQuery({
    queryKey: ['visitors', currentPage],
    queryFn: async () => {
      try {
        const response = await visitorService.list({ page: currentPage, limit: 10 });
        return response;
      } catch (error: any) {
        if (typeof error === 'object' && error !== null) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar visitantes';
          setError(errorMessage);
        } else {
          setError('Erro ao buscar visitantes');
        }
        return { data: [], meta: { total: 0, page: 1, last_page: 1 } };
      }
    }
  });

  // Query para buscar dados do visitor no modo de edição
  const { data: visitorData = null, isLoading: isDataLoading } = useQuery({
    queryKey: ['visitor', id],
    queryFn: async () => {
      if (!id) return null;
      try {
        const response = await visitorService.getById(id);
        return response;
      } catch (error: any) {
        if (typeof error === 'object' && error !== null) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar visitante';
          setError(errorMessage);
        } else {
          setError('Erro ao buscar visitante');
        }
        return null;
      }
    },
    enabled: !!id,
  });

  // Estado do modal de delete
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedVisitor, setSelectedVisitor] = useState<Visitor | null>(null);

  // Mutation para deletar visitor
  const deleteMutation = useMutation({
    mutationFn: (id: string) => visitorService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visitors'] });
      closeDeleteModal();
    },
    onError: (error: any) => {
      if (typeof error === 'object' && error !== null) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao deletar visitante';
        setError(errorMessage);
      } else {
        setError('Erro ao deletar visitante');
      }
    }
  });

  // Handlers para listagem
  const handleCreate = () => {
    navigate('/visitors/new');
  };

  const handleView = (visitor: Visitor) => {
    navigate(`/visitors/${visitor.id}`);
  };

  const handleCloseError = () => {
    setError(null);
  };

  // Handlers para modal de delete
  const openDeleteModal = (visitor: Visitor) => {
    setSelectedVisitor(visitor);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedVisitor(null);
  };

  const handleDeleteVisitor = () => {
    if (selectedVisitor?.id) {
      deleteMutation.mutate(selectedVisitor.id);
    }
  };

  // Handler para navegação
  const navigateToEdit = (id: string) => {
    navigate(`/visitors/${id}/edit`);
  };

  // Mutations para criar e atualizar visitors
  const createMutation = useMutation({
    mutationFn: (data: CreateVisitorData) => visitorService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visitors'] });
      navigate('/visitors');
    },
    onError: (error: any) => {
      if (typeof error === 'object' && error !== null) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar visitante';
        setError(errorMessage);
      } else {
        setError('Erro ao criar visitante');
      }
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVisitorData }) => 
      visitorService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['visitors'] });
      queryClient.invalidateQueries({ queryKey: ['visitor', id] });
      navigate('/visitors');
    },
    onError: (error: any) => {
      if (typeof error === 'object' && error !== null) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao atualizar visitante';
        setError(errorMessage);
      } else {
        setError('Erro ao atualizar visitante');
      }
    }
  });

  const createVisitor = async (data: CreateVisitorData) => {
    setError(null);
    await createMutation.mutateAsync(data);
  };

  const updateVisitor = async (id: string, data: UpdateVisitorData) => {
    setError(null);
    await updateMutation.mutateAsync({ id, data });
  };

  const getVisitorById = (id: string) => {
    const visitor = visitors.find((v:any) => v.id === id);
    if (!visitor) {
      throw new Error('Visitante não encontrado');
    }
    return visitor;
  };

  const visitors = visitorsResponse?.data || [];
  const totalPages = visitorsResponse?.meta.last_page || 1;

  return (
    <VisitorContext.Provider
      value={{
        // Listagem
        visitors,
        isLoading,
        loading: createMutation.isPending || updateMutation.isPending,
        error,
        handleCreate,
        handleView,
        handleCloseError,
        
        // Modal de Delete
        isDeleteModalOpen,
        selectedVisitor,
        openDeleteModal,
        closeDeleteModal,
        handleDeleteVisitor,
        isDeleting: deleteMutation.isPending,
        
        // Navegação
        navigateToEdit,

        // Form
        visitorData,
        isDataLoading,
        createVisitor,
        updateVisitor,
        isSubmitting: createMutation.isPending || updateMutation.isPending,
        getVisitorById,
        setError,
        
        // Pagination
        currentPage,
        totalPages,
        setCurrentPage,
      }}
    >
      {children}
    </VisitorContext.Provider>
  );
}